import { Button, Grid, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { useEffect } from 'react';

import { DateTimePickerInput, MultiLangCodes, TextInput } from '@components';
import { useTSD } from '@hooks/tsd';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSendPresentationNotificationFormInitializer } from './useSendPresentationNotificationFormInitializer';

interface SendPresentationNotificationProps {
  onHide(): void;
  tsdId: string;
}

export const SendPresentationNotification: React.FC<SendPresentationNotificationProps> = ({ onHide, tsdId }) => {
  const codelistSystems = useCodeListSystem({});
  const { data: tsd, isLoading } = useTSD(tsdId);

  const { form, onSubmit, submitting, buildFormValues } = useSendPresentationNotificationFormInitializer({ tsd, tsdId, onHide });

  useEffect(() => {
    if (tsd) {
      form.setValues(buildFormValues(tsd));
    }
  }, [tsd]);

  if (!tsd) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <Stack mb="xl">
          <Title order={5} fw={600}>
            Presentation data
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <DateTimePickerInput
                {...form.getInputProps('generalInfo.dateAndTimeOfPresentationOfTheGoods')}
                label="Date and time of presentation of the goods"
                size="lg"
                data-pw="generalInfo.dateAndTimeOfPresentationOfTheGoods"
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('customsOfficeOfPresentation.referenceNumber')}
                label="Customs office of presentation"
                data-pw="customsOfficeOfPresentation.referenceNumber"
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Carrier
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('carrier.name')} label="Carrier name" data-pw="carrier.name" />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('carrier.identificationNumber')} label="Carrier identification" data-pw="carrier.identificationNumber" />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Person presenting the goods
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('personPresentingTheGoods.identificationNumber')}
                label="Identification number"
                data-pw="personPresentingTheGoods.identificationNumber"
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('personPresentingTheGoods.name')} label="Name" data-pw="personPresentingTheGoods.name" />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <TextInput
                {...form.getInputProps('personPresentingTheGoods.address.streetAndNumber')}
                label="Street and number"
                data-pw="personPresentingTheGoods.address.streetAndNumber"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <MultiLangCodes
                {...form.getInputProps('personPresentingTheGoods.address.country')}
                label="Country"
                code="CL199"
                system={codelistSystems}
                data-pw="personPresentingTheGoods.address.country"
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <TextInput
                {...form.getInputProps('personPresentingTheGoods.address.postcode')}
                label="Post code"
                data-pw="personPresentingTheGoods.address.postcode"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('personPresentingTheGoods.address.city')} label="City" data-pw="personPresentingTheGoods.address.city" />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('personPresentingTheGoods.communication.phoneNumber')}
                label="Phone number"
                data-pw="personPresentingTheGoods.communication.phoneNumber"
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('personPresentingTheGoods.communication.eMailAddress')}
                label="Email address"
                data-pw="personPresentingTheGoods.communication.eMailAddress"
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </Stack>

      <Group justify="center">
        <Button type="button" onClick={onHide} disabled={submitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg" data-pw="create-btn">
          Send
        </Button>
      </Group>
    </form>
  );
};
